"use client"

import { useEffect, useMemo, useRef, useState } from "react"
// The `/pure` entry, never the bare one: the bare `@stripe/stripe-js` injects
// js.stripe.com the moment it is imported, on every page that mounts this
// component, price in view or not. `/pure` loads nothing until loadStripe runs.
import { loadStripe } from "@stripe/stripe-js/pure"
import type { Stripe } from "@stripe/stripe-js"
import { Elements, PaymentMethodMessagingElement } from "@stripe/react-stripe-js"
import { PAY_OVER_TIME_MIN_CENTS } from "@/lib/billing/pay-over-time"

/**
 * "You do not have to pay all of it today" — said where the PRICE is, not at
 * checkout.
 *
 * Owner's reasoning (2026-09-03), which is the design brief: a man reading a
 * $4,000 price decides right there whether he is out. By the time he reaches
 * checkout that decision is already made, so pay-over-time at checkout is a
 * payment method, while pay-over-time beside the price is a reason to keep
 * reading.
 *
 * EVERY WORD AND EVERY FIGURE HERE IS THE PROVIDER'S, NOT OURS. Owner, same
 * day: "The BNPL wording should be standardized. Like, we're not supposed to
 * create our own BNPL." So this file writes no copy and computes no number.
 * Two official messaging components do all of it:
 *
 *   - Stripe's PaymentMethodMessagingElement renders Klarna, Afterpay and
 *     Affirm's own approved messaging, with the instalment figures and the
 *     terms modal each provider requires.
 *   - PayPal's `messages` component does the same for PayPal Pay Later.
 *
 * The tempting shortcut — divide the price by twelve, print "$333/mo" — would
 * invent a term and a rate no provider has agreed to. Real terms depend on the
 * buyer's own approval, and Affirm alone spans 3 to 36 months at 0-36% APR.
 * We supply the amount; the provider supplies the promise.
 *
 * Both components self-gate, which satisfies the standing rule that we never
 * advertise a method a given buyer will not be offered: each renders NOTHING
 * when the buyer, the country or the amount is ineligible. At $4,000, for
 * instance, the rails with low ceilings simply do not appear.
 *
 * NEITHER PROVIDER'S SCRIPT LOADS UNTIL THE LINE IS NEAR THE READER. Stripe.js
 * and PayPal's messages SDK together are 1 to 3.6 MB of third-party script, and
 * the prices that carry this line sit thousands of pixels down their pages, so
 * loading them at mount made every visit pay for a line most never scroll to.
 * An IntersectionObserver on the wrapper starts both loads once the wrapper is
 * within LOAD_AHEAD of the viewport: a price already on screen loads at once, a
 * price below the fold loads while the reader is still a screen away, so the
 * providers' lines are normally painted before they arrive. The wrapper is the
 * same empty box until then that it was while the providers were loading, so
 * nothing is reserved: a line that does not apply to this buyer still leaves no
 * gap. A reader who jumps straight to a price (a hero button's anchor) sees the
 * lines grow in under it a moment later, a shift of about 0.005 to 0.01.
 */

const STRIPE_PK = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
const PAYPAL_CLIENT_ID = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID

/** Below this a split does not change the decision; the line would be noise.
 *  Set at $300 rather than $500 so the flagship at $497 qualifies: four
 *  instalments on it is about $124 each, which is exactly the reframe this
 *  component exists for. The providers gate themselves below their own minimums
 *  anyway, so this only decides when we bother asking them. */
const MIN_CENTS = PAY_OVER_TIME_MIN_CENTS

const BNPL_METHODS: Array<"klarna" | "afterpay_clearpay" | "affirm"> = [
  "klarna",
  "afterpay_clearpay",
  "affirm",
]

/** How far below (or above) the viewport the wrapper may be and still start the
 *  provider loads. */
const LOAD_AHEAD = "400px"

let stripeShared: Promise<Stripe | null> | null = null

/** One Stripe instance per page, shared by every price on it, created the first
 *  time a line comes near the reader. A blocked or failed Stripe.js resolves to
 *  null, which leaves the Stripe box empty, so it can never take the price
 *  section down with it (PayPal's loader below follows the same rule). */
function sharedStripe(): Promise<Stripe | null> | null {
  if (!STRIPE_PK) return null
  stripeShared ??= loadStripe(STRIPE_PK).catch(() => {
    // The `/pure` loader leaves its failed script tag in <head>. The checkout
    // loads Stripe through the bare entry, which would attach to that dead tag
    // and never settle, so it would show neither its notice nor Try again.
    if (!window.Stripe) document.querySelector('script[src^="https://js.stripe.com"]')?.remove()
    return null
  })
  return stripeShared
}

let paypalSdk: Promise<void> | null = null

/** One PayPal SDK load per page, shared by every price on it. */
function loadPayPalMessages(): Promise<void> {
  if (!PAYPAL_CLIENT_ID) return Promise.reject(new Error("no paypal client id"))
  if (paypalSdk) return paypalSdk
  paypalSdk = new Promise((resolve, reject) => {
    const s = document.createElement("script")
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      PAYPAL_CLIENT_ID,
    )}&components=messages&currency=USD`
    s.async = true
    s.addEventListener("load", () => resolve())
    s.addEventListener("error", () => reject(new Error("paypal sdk failed")))
    document.head.appendChild(s)
  })
  return paypalSdk
}

function PayPalPayLater({ amountCents, align, onDark }: { amountCents: number; align: "left" | "center"; onDark: boolean }) {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    let cancelled = false
    loadPayPalMessages()
      .then(() => {
        if (!cancelled) setReady(true)
      })
      // A blocked or failed SDK must never take the price section down with it.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  if (!ready) return null
  return (
    <div
      data-pp-message
      data-pp-amount={(amountCents / 100).toFixed(2)}
      data-pp-style-layout="text"
      data-pp-style-logo-type="inline"
      data-pp-style-text-size="12"
      data-pp-style-text-align={align}
      data-pp-style-text-color={onDark ? "white" : "black"}
    />
  )
}

export function PayOverTime({
  amountCents,
  className = "",
  align = "left",
  onDark = false,
}: {
  amountCents: number
  className?: string
  /** True inside a dark band (the closing bands paint --color-ink-band), so
   *  the providers draw their lines in light ink: Stripe's night theme with a
   *  white logo, PayPal's white text. Left alone, both providers paint dark
   *  text on the dark band and the line is unreadable. */
  onDark?: boolean
  /** Where the lines sit in their box. PayPal's component takes it as its
   *  own `text-align` style option; Stripe's messaging element exposes no
   *  alignment (its options are amount, currency, methods, country and logo
   *  colour), so its line follows the iframe and the caller centres the box. */
  align?: "left" | "center"
}) {
  const eligible = amountCents >= MIN_CENTS
  const wrapperRef = useRef<HTMLDivElement>(null)
  // False on the server and on the first client render, so hydration matches.
  const [near, setNear] = useState(false)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!eligible || near || !wrapper) return
    // A browser without IntersectionObserver gets the old behaviour: load now.
    if (typeof IntersectionObserver === "undefined") {
      const now = window.setTimeout(() => setNear(true), 0)
      return () => window.clearTimeout(now)
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { rootMargin: LOAD_AHEAD },
    )
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [eligible, near])

  const stripe = useMemo(() => (near ? sharedStripe() : null), [near])

  if (!eligible) return null

  return (
    <div ref={wrapperRef} className={`flex flex-col gap-1.5 ${className}`}>
      {stripe && (
        // Stripe's iframe fills its box and lays its line out from the left,
        // so a centred seat narrows the box to about the line's own width;
        // the live read at 1440 measured the painted line at 312px. A left
        // seat keeps the full width.
        <div className={align === "center" ? "mx-auto w-full max-w-[340px]" : undefined}>
          <Elements stripe={stripe} options={onDark ? { appearance: { theme: "night" } } : undefined}>
            <PaymentMethodMessagingElement
              options={{
                amount: amountCents,
                currency: "USD",
                countryCode: "US",
                paymentMethodTypes: BNPL_METHODS,
                ...(onDark ? { logoColor: "white" as const } : {}),
              }}
            />
          </Elements>
        </div>
      )}
      {near && <PayPalPayLater amountCents={amountCents} align={align} onDark={onDark} />}
    </div>
  )
}
